import React, { useState, useEffect } from 'react';
import { Calendar, Users, Image, Settings, BarChart3, LogOut, Wrench, Package, FileText, Tag, Home, Mail, MessageCircle, ClipboardList } from 'lucide-react';
import AdminLogin from '../AdminLogin';
import Dashboard from './Dashboard';
import AppointmentManagement from './AppointmentManagement';
import GalleryManagement from './GalleryManagement';
import AdminSettings from './AdminSettings';
import ServiceManagement from './ServiceManagement';
import PackageManagement from './PackageManagement';
import CategoryManagement from './CategoryManagement';
import HomeSettingsManagement from './HomeSettingsManagement';
import TeamManagement from './TeamManagement';
import AboutUsManagement from './AboutUsManagement';
import CustomerManagement from './CustomerManagement';
import SmtpSettingsComponent from './SmtpSettings';
import TelegramSettingsComponent from './TelegramSettings';
import SurveyManagement from './SurveyManagement';
import ApiService from '../../services/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

const AdminLayout: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [homeSettings, setHomeSettings] = useState<any>(null);
  const [websocket, setWebsocket] = useState<WebSocket | null>(null);
  const [wsConnected, setWsConnected] = useState(() => {
    const saved = localStorage.getItem('wsConnected');
    return saved === 'true';
  });
  const [wsReconnectAttempts, setWsReconnectAttempts] = useState(() => {
    const saved = localStorage.getItem('wsReconnectAttempts');
    return saved ? parseInt(saved, 10) : 0;
  });
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 saniye

  // Authentication kontrolü ve home settings yükleme
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.role === 'admin') {
          setIsAuthenticated(true);
          setUser(parsedUser);
        } else {
          handleLogout();
        }
      } catch (error) {
        handleLogout();
      }
    }

    // Home settings yükle
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        setHomeSettings(response.settings);
      } catch (error) {
        console.error('Home settings yüklenemedi:', error);
      }
    };

    loadHomeSettings();
  }, []);

  // WebSocket bağlantısı ve yeniden bağlanma sistemi
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    let reconnectTimeout: NodeJS.Timeout;
    let ws: WebSocket | null = null;

    const connectWebSocket = () => {
      try {
        const wsUrl = process.env.REACT_APP_WS_URL || 
                    (typeof window !== 'undefined' ? `ws://${window.location.hostname}:5000` : 'ws://localhost:5000');
        
        console.log('Attempting WebSocket connection to:', wsUrl);
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('WebSocket connected successfully');
          setWsConnected(true);
          setWsReconnectAttempts(0);
          setError(null);
          
          // Bağlantı durumunu localStorage'a kaydet
          localStorage.setItem('wsConnected', 'true');
          localStorage.removeItem('wsReconnectAttempts');
          
          // Başarılı bağlantı bildirimi
          if (wsReconnectAttempts > 0) {
            console.log('WebSocket reconnection successful');
          }
          
          // Authenticated kullanıcı olarak katıl
          if (ws) {
            ws.send(JSON.stringify({ type: 'join-admin' }));
          }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('WebSocket message received:', data);
            
            // Bağlantı onayı
            if (data.type === 'connection-confirmed') {
              console.log('WebSocket connection confirmed:', data.message);
            }
            
            // Pong mesajı
            if (data.type === 'pong') {
              const latency = Date.now() - data.timestamp;
              console.log(`WebSocket ping-pong latency: ${latency}ms`);
            }
            
            // Randevu güncellemelerini işle
            if (data.type === 'appointments-updated') {
              // Component'lerde bu veriyi kullanacağız
              window.dispatchEvent(new CustomEvent('appointments-updated', { detail: data }));
            }
          } catch (error) {
            console.error('WebSocket message parse error:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          setWsConnected(false);
        };

        ws.onclose = (event) => {
          console.log('WebSocket disconnected:', event.code, event.reason);
          setWsConnected(false);
          
          // Bağlantı durumunu localStorage'a kaydet
          localStorage.setItem('wsConnected', 'false');
          localStorage.setItem('wsReconnectAttempts', wsReconnectAttempts.toString());
          
          // Eğer manuel olarak kapatılmadıysa ve yeniden bağlanma denemeleri kalmışsa
          if (event.code !== 1000 && wsReconnectAttempts < maxReconnectAttempts) {
            console.log(`Attempting to reconnect... (${wsReconnectAttempts + 1}/${maxReconnectAttempts})`);
            setWsReconnectAttempts(prev => prev + 1);
            
            reconnectTimeout = setTimeout(() => {
              connectWebSocket();
            }, reconnectDelay);
          } else if (wsReconnectAttempts >= maxReconnectAttempts) {
            console.log('Max reconnection attempts reached');
            setError('WebSocket bağlantısı kurulamadı. Lütfen sayfayı yenileyin.');
          }
        };

        setWebsocket(ws);
      } catch (error) {
        console.error('WebSocket connection error:', error);
        setWsConnected(false);
        
        // Bağlantı hatası durumunda da yeniden deneme
        if (wsReconnectAttempts < maxReconnectAttempts) {
          setWsReconnectAttempts(prev => prev + 1);
          reconnectTimeout = setTimeout(() => {
            connectWebSocket();
          }, reconnectDelay);
        }
      }
    };

    // İlk bağlantıyı kur
    connectWebSocket();

    // Cleanup function
    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (ws) {
        ws.close(1000, 'Component unmounting');
      }
    };
  }, [isAuthenticated, user, wsReconnectAttempts, maxReconnectAttempts, reconnectDelay]);

  const handleLogin = (token: string) => {
    setIsAuthenticated(true);
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('wsConnected');
    localStorage.removeItem('wsReconnectAttempts');
    setIsAuthenticated(false);
    setUser(null);
  };

  // WebSocket bağlantısını test etmek için fonksiyon
  const testWebSocketConnection = () => {
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      websocket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      console.log('WebSocket ping sent');
    } else {
      console.log('WebSocket is not connected');
    }
  };

  // Debug modu için state
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, component: Dashboard },
    { id: 'appointments', label: 'Randevular', icon: Calendar, component: AppointmentManagement },
    { id: 'customers', label: 'Müşteriler', icon: Users, component: CustomerManagement },
    { id: 'services', label: 'Hizmetler', icon: Wrench, component: ServiceManagement },
    { id: 'categories', label: 'Kategoriler', icon: Tag, component: CategoryManagement },
    { id: 'packages', label: 'Paketler', icon: Package, component: PackageManagement },
    { id: 'gallery', label: 'Galeri', icon: Image, component: GalleryManagement },
    { id: 'team', label: 'Ekip', icon: Users, component: TeamManagement },
    { id: 'about', label: 'Hakkımızda', icon: FileText, component: AboutUsManagement },
    { id: 'home-settings', label: 'Ana Sayfa', icon: Home, component: HomeSettingsManagement },
    { id: 'smtp', label: 'Mail Ayarları', icon: Mail, component: SmtpSettingsComponent },
    { id: 'telegram', label: 'Telegram Bot', icon: MessageCircle, component: TelegramSettingsComponent },
    { id: 'surveys', label: 'Anketler', icon: ClipboardList, component: SurveyManagement },
    { id: 'settings', label: 'Ayarlar', icon: Settings, component: AdminSettings }
  ];

  // Login ekranı göster
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || Dashboard;

  return (
    <div className="min-h-screen admin-panel" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
      {/* Error Message */}
      {error && (
        <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded mb-4 mx-4">
          <span className="block sm:inline">{error}</span>
          <button 
            onClick={() => setError(null)}
            className="float-right text-red-200 hover:text-red-100"
          >
            ×
          </button>
        </div>
      )}

      {/* WebSocket Connection Status */}
      <div className="fixed top-4 right-4 z-50 group">
        <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg shadow-lg transition-all duration-300 cursor-help ${
          wsConnected 
            ? 'bg-green-900 border border-green-700 text-green-200' 
            : wsReconnectAttempts > 0
              ? 'bg-yellow-900 border border-yellow-700 text-yellow-200'
              : 'bg-red-900 border border-red-700 text-red-200'
        }`}>
          <div className={`w-2 h-2 rounded-full animate-pulse ${
            wsConnected 
              ? 'bg-green-400' 
              : wsReconnectAttempts > 0
                ? 'bg-yellow-400'
                : 'bg-red-400'
          }`}></div>
          <span className="text-sm font-medium">
            {wsConnected 
              ? 'Bağlantı Aktif' 
              : wsReconnectAttempts > 0 
                ? `Yeniden Bağlanıyor... (${wsReconnectAttempts}/${maxReconnectAttempts})`
                : 'Bağlantı Kesildi'
            }
          </span>
          {!wsConnected && wsReconnectAttempts >= maxReconnectAttempts && (
            <button
              onClick={() => {
                setWsReconnectAttempts(0);
                setError(null);
                localStorage.removeItem('wsReconnectAttempts');
                // Force reconnection by triggering useEffect
                window.location.reload();
              }}
              className="ml-2 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              Yeniden Dene
            </button>
          )}
        </div>
        
        {/* Tooltip */}
        <div className="absolute right-0 top-full mt-2 w-64 bg-gray-900 border border-gray-700 rounded-lg p-3 text-sm text-gray-300 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
          <div className="font-medium mb-2">WebSocket Bağlantı Durumu</div>
          <div className="space-y-1 text-xs">
            <div>Durum: {wsConnected ? 'Aktif' : 'Kesildi'}</div>
            <div>Yeniden Bağlanma: {wsReconnectAttempts}/{maxReconnectAttempts}</div>
            <div>Son Güncelleme: {new Date().toLocaleTimeString('tr-TR')}</div>
            {websocket && (
              <div>Bağlantı Kodu: {websocket.readyState === 0 ? 'Bağlanıyor' : 
                                   websocket.readyState === 1 ? 'Açık' : 
                                   websocket.readyState === 2 ? 'Kapanıyor' : 'Kapalı'}</div>
            )}
            <button
              onClick={() => setShowDebugInfo(!showDebugInfo)}
              className="mt-2 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded transition-colors"
            >
              {showDebugInfo ? 'Debug Kapat' : 'Debug Aç'}
            </button>
          </div>
        </div>
      </div>

      {/* Debug Panel */}
      {showDebugInfo && (
        <div className="fixed top-20 right-4 z-40 w-80 bg-gray-900 border border-gray-700 rounded-lg p-4 text-sm text-gray-300">
          <div className="font-medium mb-3">WebSocket Debug Bilgileri</div>
          <div className="space-y-2 text-xs">
            <div>Bağlantı Durumu: {wsConnected ? '✅ Aktif' : '❌ Kesildi'}</div>
            <div>Yeniden Bağlanma: {wsReconnectAttempts}/{maxReconnectAttempts}</div>
            <div>WebSocket State: {websocket ? 
              (websocket.readyState === 0 ? '🔄 Bağlanıyor' : 
               websocket.readyState === 1 ? '✅ Açık' : 
               websocket.readyState === 2 ? '🔄 Kapanıyor' : '❌ Kapalı') : '❌ Yok'
            }</div>
            <div>Son Güncelleme: {new Date().toLocaleTimeString('tr-TR')}</div>
            <div className="pt-2 border-t border-gray-700">
              <button
                onClick={testWebSocketConnection}
                className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 rounded transition-colors mr-2"
              >
                Ping Test
              </button>
              <button
                onClick={() => {
                  setWsReconnectAttempts(0);
                  setError(null);
                  localStorage.removeItem('wsReconnectAttempts');
                  window.location.reload();
                }}
                className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 rounded transition-colors"
              >
                Yeniden Bağlan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="p-4 rounded-lg border" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto"></div>
            <p className="mt-2 text-gray-300">Yükleniyor...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="shadow-sm border-b" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-bold text-white">Yönetim Paneli</h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-300">Hoş geldiniz, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Çıkış</span>
              </button>
              <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row space-y-8 lg:space-y-0 lg:space-x-8">
          {/* Main Content - Sola taşındı */}
          <div className="flex-1 order-2 lg:order-1">
            <ActiveComponent 
              setLoading={setLoading}
              setError={setError}
              isAuthenticated={isAuthenticated}
              websocket={websocket}
            />
          </div>

          {/* Sidebar - Sağa taşındı */}
          <div className="w-full lg:w-64 rounded-lg shadow-sm p-6 border order-1 lg:order-2" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937', borderColor: homeSettings?.secondaryBackgroundColor || '#374151' }}>
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;